import { ZodSchema } from "zod";
import { getApiBaseUrl, isDebugMode } from "../../config/environment-configuration";
import { toastUtils } from "../toast";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(status: number, message: string, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

// ✅ Gestion centralisée des tokens côté client
let authToken: string | undefined = undefined;
let refreshToken: string | undefined = undefined;

// 🔒 Verrou pour éviter les refresh simultanés
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: boolean) => void; reject: (reason?: any) => void }> = [];

// Fonction pour traiter la file d'attente après refresh (on passe un boolean : succès ou échec)
function processQueue(error: Error | null, success: boolean) {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(success);
    }
  });
  failedQueue = [];
}

export const tokenManager = {
  setTokens: (accessToken: string, refreshTokenValue?: string) => {
    authToken = accessToken;

    // Cookie pour access token
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    document.cookie = `jwt=${accessToken}; path=/; SameSite=Strict; Secure=${!isLocalhost}`;

    if (refreshTokenValue) {
      refreshToken = refreshTokenValue;
      localStorage.setItem('refreshToken', refreshTokenValue);
      document.cookie = `refresh_jwt=${refreshTokenValue}; path=/; SameSite=Strict; Secure=${!isLocalhost}`;
    }

    try {
      window.dispatchEvent(new CustomEvent('auth:changed', { detail: { isAuthenticated: true } }));
    } catch {}
  },

  setToken: (token: string) => {
    tokenManager.setTokens(token);
  },

  getToken: () => authToken ?? document.cookie.match(/(^| )jwt=([^;]+)/)?.[2],

  getRefreshToken: () => refreshToken ?? localStorage.getItem('refreshToken') ?? document.cookie.match(/(^| )refresh_jwt=([^;]+)/)?.[2],

  clearTokens: () => {
    authToken = undefined;
    refreshToken = undefined;
    localStorage.removeItem('refreshToken');
    document.cookie = `jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    document.cookie = `refresh_jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

    try {
      window.dispatchEvent(new CustomEvent('auth:changed', { detail: { isAuthenticated: false } }));
    } catch {}
  },

  clearToken: () => {
    tokenManager.clearTokens();
  },

  isTokenExpired: (token?: string): boolean => {
    const tokenToCheck = token || tokenManager.getToken();
    if (!tokenToCheck) return true;

    try {
      const base64Url = tokenToCheck.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload);
      const currentTime = Math.floor(Date.now() / 1000);

      return payload.exp < currentTime;
    } catch (error) {
      console.warn("Erreur lors de la vérification du token:", error);
      return true;
    }
  },

  getTokenInfo: (token?: string): any => {
    const tokenToCheck = token || tokenManager.getToken();
    if (!tokenToCheck) return null;

    try {
      const base64Url = tokenToCheck.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload);
      const currentTime = Math.floor(Date.now() / 1000);

      return {
        ...payload,
        isExpired: payload.exp < currentTime,
        expiresAt: new Date(payload.exp * 1000),
        timeUntilExpiry: payload.exp - currentTime
      };
    } catch (error) {
      console.warn("Erreur lors du décodage du token:", error);
      return null;
    }
  },

  forceExpiration: (): void => {
    if (isDebugMode()) {
      console.warn("🔒 Forçage de l'expiration du token (mode développement)");
      handleTokenExpiration();
    }
  },

  // 🔄 Rafraîchissement du token avec verrou pour éviter les appels multiples
  refreshAccessToken: async (): Promise<boolean> => {
    if (isRefreshing) {
      // Attendre la fin du refresh en cours
      return new Promise<boolean>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      });
    }

    isRefreshing = true;
    const currentRefreshToken = tokenManager.getRefreshToken();

    if (!currentRefreshToken) {
      isRefreshing = false;
      processQueue(new Error("No refresh token"), false);
      return false;
    }

    try {
      if (isDebugMode()) {
        console.log("🔄 Tentative de rafraîchissement du token...");
      }

      const { endpoints } = await import('../endpoints');

      const response = await fetch(buildApiUrl(endpoints.refreshToken), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentRefreshToken}`
        },
        credentials: 'include' // Refresh token always needs credentials
      });

      if (!response.ok) {
        isRefreshing = false;
        processQueue(new Error("Refresh failed"), false);
        return false;
      }

      const data = await response.json();
      let tokenData = data;
      if (data && typeof data === 'object' && 'success' in data && data.success && data.data) {
        tokenData = data.data;
      }

      if (tokenData.accessToken) {
        tokenManager.setTokens(tokenData.accessToken, tokenData.refreshToken || currentRefreshToken);
        processQueue(null, true); // ← succès = true
        if (isDebugMode()) {
          console.log("✅ Token rafraîchi avec succès");
        }
        return true;
      }

      processQueue(new Error("Invalid refresh response"), false);
      return false;
    } catch (error) {
      processQueue(error as Error, false);
      if (isDebugMode()) {
        console.error("❌ Erreur lors du rafraîchissement du token:", error);
      }
      return false;
    } finally {
      isRefreshing = false;
    }
  },
};

interface ApiOptions<T = any> {
  body?: T;
  headers?: Record<string, string>;
  token?: string;
  schema?: ZodSchema;
  isPublicRoute?: boolean;
  responseType?: 'json' | 'blob' | 'text';
}

function buildApiUrl(endpoint: string): string {
  if (typeof endpoint !== 'string') {
    console.error('buildApiUrl received non-string endpoint:', endpoint);
    throw new Error(`buildApiUrl expects a string, but received: ${typeof endpoint}`);
  }

  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }

  const baseUrl = getApiBaseUrl();
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${baseUrl}/${cleanEndpoint}`;
}

async function handleTokenExpiration(): Promise<void> {
  const refreshSuccess = await tokenManager.refreshAccessToken();

  if (refreshSuccess) {
    if (isDebugMode()) {
      console.log("✅ Token rafraîchi automatiquement, pas de redirection nécessaire");
    }
    return;
  }

  tokenManager.clearTokens();
  window.dispatchEvent(new CustomEvent('authChange', { detail: { isAuthenticated: false, user: null } }));
  toastUtils.sessionExpired();

  setTimeout(() => {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, 2000);
}

function isTokenExpiredError(status: number, errorData: any): boolean {
  if (status !== 401) return false;

  if (errorData && typeof errorData === 'object') {
    const message = errorData.message || '';
    const errors = errorData.errors || [];
    const allMessages = [message, ...errors].join(' ').toLowerCase();

    const nonTokenKeywords = [
      'email', 'vérifier', 'vérifiez', 'validation', 'confirmer', 'confirmez',
      'activer', 'activez', 'boîte', 'inbox', 'verify', 'confirm', 'activate',
      'credentials', 'identifiants', 'mot de passe', 'password'
    ];

    if (nonTokenKeywords.some(keyword => allMessages.includes(keyword))) {
      return false;
    }
  }

  return true;
}

function isValidationError(status: number): boolean {
  return status === 422;
}

function isMaintenanceError(status: number): boolean {
  return status === 503;
}

function handleMaintenanceError(): void {
  toastUtils.maintenanceMode();

  if (isDebugMode()) {
    console.warn("🚧 Serveur en maintenance (503) - Redirection vers la page de maintenance");
  }

  setTimeout(() => {
    if (window.location.pathname !== '/maintenance') {
      window.location.href = '/maintenance';
    }
  }, 2000);
}

function createContextualErrorMessage(status: number, originalMessage: string, errorData?: any): string {
  switch (status) {
    case 401:
      if (isTokenExpiredError(status, errorData)) {
        return "Session expirée. Reconnexion automatique en cours...";
      }
      return originalMessage;
    case 422:
      return `Erreur de validation : ${originalMessage}`;
    case 500:
      if (errorData && errorData.errors && errorData.errors.length > 0) {
        return errorData.errors.join(', ');
      }
      return originalMessage || "Erreur serveur interne";
    case 503:
      return "Service temporairement indisponible";
    default:
      return originalMessage;
  }
}

export async function apiClient<TResponse = any, TBody = any>(
  url: string,
  method: HttpMethod,
  options: ApiOptions<TBody> = {}
): Promise<TResponse> {
  const { body, headers, token, schema, isPublicRoute = false, responseType = 'json' } = options;
  let auth = token ?? tokenManager.getToken();
  const fullUrl = buildApiUrl(url);

  if (auth && tokenManager.isTokenExpired(auth)) {
    if (isDebugMode()) {
      console.warn("🔒 Token expiré détecté avant la requête, tentative de refresh...");
    }

    const refreshSuccess = await tokenManager.refreshAccessToken();
    if (refreshSuccess) {
      auth = tokenManager.getToken();
    }
  }

  try {
    const isFormData = body instanceof FormData;

    const requestHeaders: Record<string, string> = {
      ...(auth ? { Authorization: `Bearer ${auth}` } : {}),
      ...headers,
    };

    if (responseType !== 'blob' && !isFormData) {
      requestHeaders["Content-Type"] = "application/json";
    }

    let requestBody: BodyInit | undefined;
    if (body) {
      requestBody = isFormData ? body : JSON.stringify(body);
    }

    const res = await fetch(fullUrl, {
      method,
      headers: requestHeaders,
      body: requestBody,
      credentials: isPublicRoute ? 'omit' : 'include'
    });

    if (!res.ok) {
      if (isMaintenanceError(res.status)) {
        handleMaintenanceError();
        throw new ApiError(503, "Service en maintenance", { maintenance: true });
      }

      let errorMsg = res.statusText;
      let errorDetails = null;

      if (responseType === 'blob') {
        try {
          const clonedRes = res.clone();
          const errorData = await clonedRes.json();
          if (errorData && 'message' in errorData) {
            throw new ApiError(res.status, errorData.message || res.statusText, errorData);
          }
        } catch {
          throw new ApiError(res.status, res.statusText || "Erreur lors du téléchargement du fichier");
        }
      }

      try {
        const errorData = await res.json();

        if (isTokenExpiredError(res.status, errorData) && !isPublicRoute) {
          const refreshSuccess = await tokenManager.refreshAccessToken();
          if (refreshSuccess) {
            const newAuth = tokenManager.getToken();
            const retryResponse = await fetch(fullUrl, {
              method,
              headers: {
                "Content-Type": "application/json",
                ...(newAuth ? { Authorization: `Bearer ${newAuth}` } : {}),
                ...headers,
              },
              body: body ? JSON.stringify(body) : undefined,
              credentials: isPublicRoute ? 'omit' : 'include'
            });

            if (retryResponse.ok) {
              let retryData: any = null;
              try {
                retryData = await retryResponse.json();
              } catch {
                return (null as unknown) as TResponse;
              }

              if (retryData && typeof retryData === 'object' && 'success' in retryData) {
                if (!retryData.success) {
                  const errorMessages = retryData.errors || [];
                  const errorMessage = errorMessages.length > 0 ? errorMessages.join(', ') : retryData.message || 'Une erreur est survenue';
                  throw new ApiError(retryResponse.status, errorMessage, retryData);
                }
                retryData = retryData.data;
              }

              console.log(`${fullUrl}`, retryData);
              return retryData as TResponse;
            }
          }

          await handleTokenExpiration();
          throw new ApiError(401, "Session expirée", { expired: true });
        }

        if (errorData && typeof errorData === 'object' && 'success' in errorData) {
          if (!errorData.success && errorData.errors && errorData.errors.length > 0) {
            errorMsg = errorData.errors.join(', ');
          } else if (errorData.message) {
            errorMsg = errorData.message;
          }
        } else if (errorData.message) {
          errorMsg = errorData.message;
        }

        errorDetails = errorData;
      } catch {}

      console.error(`❌ ${fullUrl}`, errorMsg || res.statusText);

      const contextualMessage = createContextualErrorMessage(res.status, errorMsg, errorDetails);
      throw new ApiError(res.status, contextualMessage, errorDetails);
    }

    if (responseType === 'blob') {
      // Vérifier le Content-Type pour détecter si c'est vraiment un fichier ou du JSON
      const contentType = res.headers.get('content-type') || '';
      
      // Si c'est du JSON, parser et vérifier success
      if (contentType.includes('application/json')) {
        const data = await res.json();
        
        // Vérifier si l'API retourne une structure avec success/errors
        if (data && typeof data === 'object' && 'success' in data) {
          if (!data.success) {
            // Extraire les erreurs du champ errors
            const errorMessages = data.errors || [];
            const errorMessage = errorMessages.length > 0 
              ? errorMessages.join(', ') 
              : data.message || 'Une erreur est survenue lors du téléchargement';
            
            // Log erreur
            console.error(`❌ ${fullUrl}`, errorMessage);
            
            throw new ApiError(res.status, errorMessage, data);
          }
          
          // Si success est true, extraire uniquement data
          // Mais si data.data n'est pas un blob, c'est une erreur
          if (data.data === false || data.data === null) {
            throw new ApiError(res.status, 'Le fichier n\'est pas disponible', data);
          }
        }
        
        // Si ce n'est pas la structure attendue, lancer une erreur
        throw new ApiError(res.status, 'Réponse JSON inattendue lors du téléchargement d\'un fichier', data);
      }
      
      // Pour les vrais fichiers (PDF, images, etc.), retourner directement le blob
      const blob = await res.blob();
      
      // Vérifier que le blob n'est pas vide
      if (blob.size === 0) {
        throw new ApiError(res.status, 'Le fichier téléchargé est vide', null);
      }
      
      // Log succès
      console.log(`${fullUrl}`, { type: 'blob', size: blob.size, contentType });
      return blob as TResponse;
    }

    if (responseType === 'text') {
      const text = await res.text();
      console.log(`${fullUrl}`, text);
      return text as TResponse;
    }

    let data: any = null;
    try {
      data = await res.json();
    } catch {
      return (null as unknown) as TResponse;
    }

    if (data && typeof data === 'object' && 'success' in data) {
      if (!data.success) {
        const errorMessages = data.errors || [];
        const errorMessage = errorMessages.length > 0 ? errorMessages.join(', ') : data.message || 'Une erreur est survenue';
        console.error(`❌ ${fullUrl}`, errorMessage);
        throw new ApiError(res.status, errorMessage, data);
      }
      data = data.data;
    }

    console.log(`${fullUrl}`, data);
    return data as TResponse;
  } catch (err: any) {
    if (err instanceof ApiError) throw err;
    console.error(`❌ ${fullUrl}`, err?.message || "Network error or server unreachable");
    throw new ApiError(0, "Network error or server unreachable", err?.message);
  }
}

export const api = {
  get: <T>(url: string, options?: { schema?: ZodSchema; isPublicRoute?: boolean; responseType?: 'json' | 'blob' | 'text' }) =>
    apiClient<T>(url, "GET", options || {}),
  post: <T, B>(url: string, body: B, options?: { schema?: ZodSchema; isPublicRoute?: boolean }) =>
    apiClient<T, B>(url, "POST", { body, ...(options || {}) }),
  put: <T, B>(url: string, body: B, options?: { schema?: ZodSchema; isPublicRoute?: boolean }) =>
    apiClient<T, B>(url, "PUT", { body, ...(options || {}) }),
  patch: <T, B>(url: string, body: B, options?: { schema?: ZodSchema; isPublicRoute?: boolean }) =>
    apiClient<T, B>(url, "PATCH", { body, ...(options || {}) }),
  delete: <T>(url: string, options?: { schema?: ZodSchema; isPublicRoute?: boolean }) =>
    apiClient<T>(url, "DELETE", options || {}),
};